import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAnnotationCanvas } from "../hooks/useAnnotationCanvas";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Cursor, PencilSimple, Square, Highlighter, ArrowUpRight, LineSegment, Circle, Crop, TextT, Eraser, ArrowLeft, ArrowUUpLeft, Check, X,
} from "@phosphor-icons/react";

const COLORS = ["#FF3333", "#FFD700", "#0A0A0A", "#2563EB", "#16A34A"];
const TOOL_DEFS = [
  { id: "select", icon: Cursor },
  { id: "pen", icon: PencilSimple },
  { id: "eraser", icon: Eraser },
  { id: "rect", icon: Square },
  { id: "circle", icon: Circle },
  { id: "highlight", icon: Highlighter },
  { id: "line", icon: LineSegment },
  { id: "arrow", icon: ArrowUpRight },
  { id: "text", icon: TextT },
  { id: "crop", icon: Crop },
];

export default function CaptureEditor() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const TOOLS = TOOL_DEFS.map((tool) => ({ ...tool, label: t(`editor.tools.${tool.id}`) }));
  const [loading, setLoading] = useState(true);
  const [screenshot, setScreenshot] = useState(null);
  const [activeTool, setActiveTool] = useState("select");
  const [color, setColor] = useState(COLORS[0]);
  const [title, setTitle] = useState("");
  const [comment, setComment] = useState("");
  const [tags, setTags] = useState([]);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const { canvasElRef, clearAnnotations, exportDataUrl, hasPendingCrop, confirmCrop, cancelCrop, undo, canUndo } = useAnnotationCanvas(screenshot, activeTool, color);

  useEffect(() => {
    api.get("/tags").then(({ data }) => setTags(data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get(`/captures/${id}`)
      .then(({ data }) => {
        setTitle(data.title || "");
        setComment(data.comment || "");
        setSelectedTagIds(data.tags || []);
        setScreenshot(data.image_data);
        setLoading(false);
      })
      .catch(() => {
        toast.error(t("editor.notFoundError"));
        navigate("/dashboard");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const toggleTag = (tagId) => {
    setSelectedTagIds((prev) => (prev.includes(tagId) ? prev.filter((t) => t !== tagId) : [...prev, tagId]));
  };

  const handleConfirmCrop = () => {
    const cropped = confirmCrop();
    if (cropped) {
      setScreenshot(cropped);
      setActiveTool("select");
    } else {
      toast.error(t("editor.cropTooSmallError"));
    }
  };

  const handleCancelCrop = () => {
    cancelCrop();
    setActiveTool("select");
  };

  // "返回" auto-saves the current annotation state before navigating away.
  // Any pending (unconfirmed) crop selection is discarded rather than
  // silently applied, since cropping is destructive to out-of-region content.
  const handleBack = async () => {
    if (hasPendingCrop) cancelCrop();
    const dataUrl = exportDataUrl();
    if (!dataUrl) {
      navigate("/dashboard");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/captures/${id}`, {
        title: title.trim() || t("editor.untitled"),
        comment,
        tags: selectedTagIds,
        image_data: dataUrl,
      });
      toast.success(t("editor.savedSuccess"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
      navigate("/dashboard");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="capture-editor-loading">
        <p className="text-muted-foreground font-mono-tag text-xs">{t("editor.loading")}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background grain-overlay overflow-x-hidden" data-testid="capture-editor-page">
      <header className="border-b border-border p-4 flex items-center justify-between sticky top-0 bg-background/95 backdrop-blur-xl z-10">
        <button data-testid="back-to-dashboard-button" disabled={saving} onClick={handleBack} className="flex items-center gap-2 text-sm font-semibold hover:text-primary transition-colors disabled:opacity-50">
          <ArrowLeft size={18} /> {saving ? t("editor.savingBack") : t("editor.back")}
        </button>
        <p className="font-mono-tag text-xs text-primary">{t("editor.tagline")}</p>
      </header>

      <div className="max-w-5xl mx-auto p-6 md:p-10">
        <div className="grid md:grid-cols-[auto_1fr] gap-6 min-w-0">
          <div className="relative min-w-0">
            <div className="flex md:flex-col gap-2 border border-border bg-card p-3 h-fit overflow-x-auto md:overflow-x-visible max-w-full">
              {TOOLS.map((tool) => (
                <button
                  key={tool.id}
                  data-testid={`canvas-tool-${tool.id}`}
                  onClick={() => setActiveTool(tool.id)}
                  title={tool.label}
                  className={`h-10 w-10 shrink-0 flex items-center justify-center border transition-colors ${activeTool === tool.id ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}
                >
                  <tool.icon size={18} />
                </button>
              ))}
              <div className="h-px md:h-px w-px md:w-full bg-border my-1 shrink-0" />
              {COLORS.map((c) => (
                <button
                  key={c}
                  data-testid={`canvas-color-${c.replace("#", "")}`}
                  onClick={() => setColor(c)}
                  style={{ backgroundColor: c }}
                  className={`h-10 w-10 shrink-0 border-2 transition-transform ${color === c ? "scale-90 border-foreground" : "border-transparent"}`}
                />
              ))}
              <div className="h-px md:h-px w-px md:w-full bg-border my-1 shrink-0" />
              <button
                data-testid="canvas-undo-button"
                onClick={undo}
                disabled={!canUndo}
                title={t("editor.undoTooltip")}
                className="h-10 w-10 shrink-0 flex items-center justify-center border border-border hover:bg-secondary transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              >
                <ArrowUUpLeft size={18} />
              </button>
              <button data-testid="canvas-clear-button" onClick={clearAnnotations} title={t("editor.clearTooltip")} className="h-10 w-10 shrink-0 flex items-center justify-center border border-border font-mono-tag text-[10px] hover:bg-secondary transition-colors">
                {t("editor.clear")}
              </button>
            </div>
            {/* Mobile-only fade hinting the toolbar scrolls horizontally (it holds
                17 buttons total, far more than a phone width can show at once). */}
            <div className="md:hidden pointer-events-none absolute top-0 right-0 h-full w-8 bg-gradient-to-l from-card to-transparent" />
          </div>

          <div className="space-y-6 min-w-0">
            <div className="relative border-2 border-foreground brutal-shadow bg-white max-w-full overflow-x-auto touch-pan-x">
              <canvas ref={canvasElRef} data-testid="annotation-canvas" />
            </div>

            {hasPendingCrop && (
              <div className="flex items-center gap-3 border border-primary bg-primary/10 px-4 py-3" data-testid="crop-confirm-bar">
                <p className="text-sm font-medium flex-1">{t("editor.cropHint")}</p>
                <Button data-testid="crop-confirm-button" onClick={handleConfirmCrop} className="rounded-none gap-2 bg-primary hover:bg-primary/90 font-semibold">
                  <Check size={16} weight="bold" /> {t("editor.confirmCrop")}
                </Button>
                <Button data-testid="crop-cancel-button" variant="outline" onClick={handleCancelCrop} className="rounded-none gap-2">
                  <X size={16} weight="bold" /> {t("editor.cancelCrop")}
                </Button>
              </div>
            )}

            <div className="space-y-4 border border-border p-6 bg-card">
              <div className="space-y-2">
                <Label htmlFor="capture-title">{t("editor.titleLabel")}</Label>
                <Input id="capture-title" data-testid="capture-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("editor.titlePlaceholder")} className="rounded-none" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="capture-comment">{t("editor.commentLabel")}</Label>
                <Textarea id="capture-comment" data-testid="capture-comment-textarea" value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t("editor.commentPlaceholder")} className="rounded-none" />
              </div>
              <div className="space-y-2">
                <Label>{t("editor.tagsLabel")}</Label>
                <div className="flex flex-wrap gap-2">
                  {tags.length === 0 && <p className="text-xs text-muted-foreground">{t("editor.noTagsHint")}</p>}
                  {tags.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      data-testid={`editor-tag-toggle-${t.id}`}
                      onClick={() => toggleTag(t.id)}
                      className={`font-mono-tag text-[10px] px-2 py-1 border transition-colors ${selectedTagIds.includes(t.id) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}
                    >
                      {t.name}
                    </button>
                  ))}
                </div>
              </div>
              <p className="font-mono-tag text-[10px] text-muted-foreground">{t("editor.autosaveHint")}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
